import { RETRY_BASE_ATTEMPTS, RETRY_BASE_TIME } from "./Constants";

export const delayPromise = async (ms: number): Promise<any> => new Promise(resolve => setTimeout(resolve, ms));

type FetchWithRetryOptions = { attemptsLeft: number, debugName?: string, initialAttempts: number };
export async function fetchWithRetry(url: string, opts: FetchWithRetryOptions): Promise<Response> {
	let response: Response;
	try {
		response = await fetch(url);

		if (!response.ok) {
			const errorMessage = `[${opts?.debugName ?? "fetchWithRetry"}] Response status not OK`;
			console.warn(errorMessage, response);
			throw new Error(errorMessage);
		}

		return response;
	} catch (error) {
		if (opts.attemptsLeft - 1 > 0) {
			// retry
			const currentAttempt = opts.initialAttempts + 1 - opts.attemptsLeft;
			await delayForAttempt(currentAttempt);

			opts.attemptsLeft--;

			return fetchWithRetry(url, opts);
		}

		// that was final attempt and it failed
		console.error("Cannot load", url, error);
		throw new Error(`[${opts?.debugName ?? "fetchWithRetry"}] Cannot load resource`);
	}
}

export async function fetchJsonWithRetry<ResponseData extends object>(url: string, optsOrDebugName: string | FetchWithRetryOptions = "fetchWithRetry"): Promise<ResponseData> {
	let opts: FetchWithRetryOptions;

	if (typeof optsOrDebugName === "string") {
		opts = {
			initialAttempts: RETRY_BASE_ATTEMPTS,
			attemptsLeft: RETRY_BASE_ATTEMPTS,
			debugName: optsOrDebugName,
		};
	} else {
		opts = optsOrDebugName;
	}

	const response = await fetchWithRetry(url, opts);

	try {
		return await response.json();
	} catch (error) {
		if (opts.attemptsLeft - 1 > 0) {
			// json stream parsing failed
			// retry whole thing
			const currentAttempt = opts.initialAttempts + 1 - opts.attemptsLeft;
			await delayForAttempt(currentAttempt);

			opts.attemptsLeft--;
			return fetchJsonWithRetry(url, opts);
		}

		console.error("Unable to parse", url, error);
		throw new Error(`[${opts?.debugName ?? 'fetchWithRetry'}] Cannot parse JSON`);
	}
}

export async function delayForAttempt(currentAttempt: number) {
	// 3, 9 sec by default
	const ms = Math.pow(3, currentAttempt) * RETRY_BASE_TIME;
	await delayPromise(ms);
}
